import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  makeStyles,
  Divider
} from "@material-ui/core";
import { TaxBreakdownModalProps, ProcessedTaxData, ProcessedTaxItem } from "./TaxBreakdownType";
import { useTaxBreakdownModal } from "./useTaxBreakdownModal";
import { formatTaxAmount, formatTaxLine } from "./taxBreakdownUtils";
import translate from "../../i18n/Translator";

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 450,
      maxWidth: 550,
      maxHeight: '90vh',
      borderRadius: 8,
    }
  },
  dialogTitle: {
    padding: theme.spacing(3, 3, 2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    textAlign: "justify"
  },
  titleText: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
    textAlign: "justify"
  },
  dialogContent: {
    padding: theme.spacing(3),
    '&:first-child': {
      paddingTop: theme.spacing(3)
    },
    textAlign: "justify"
  },
  explanationText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    marginBottom: theme.spacing(3),
    textAlign: "justify"
  },
  subtitleText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.4,
    marginBottom: theme.spacing(2),
    textAlign: "justify"
  },
  currencySection: {
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0
    }
  },
  currencyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  currencyTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  currencyTotal: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  taxItemsContainer: {
    marginLeft: theme.spacing(0),
  },
  taxItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
    borderBottom: 'none',
  },
  taxLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    flex: 1,
  },
  taxAmount: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textAlign: 'right',
    minWidth: 120,
  },
  cancellationSection: {
    marginTop: theme.spacing(2),
    marginLeft: 0,
  },
  cancellationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
  },
  cancellationTitle: {
    fontWeight: 500,
    fontSize: '0.875rem',
    color: '#ff9800', 
  },
  cancellationTotal: {
    fontWeight: 500,
    fontSize: '0.875rem',
    color: '#ff9800', 
  },
  cancellationItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
    marginLeft: 0,
  },
  cancellationLabel: {
    fontSize: '0.875rem',
    color: '#ff9800', 
    flex: 1,
  },
  cancellationAmount: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#ff9800', 
    textAlign: 'right',
    minWidth: 120,
  },
  dividerSection: {
    margin: theme.spacing(2, 0),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  dialogActions: {
    padding: theme.spacing(2, 3, 3, 3),
    justifyContent: 'flex-end',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  acceptButton: {
    minWidth: 100,
    textTransform: 'none',
    borderRadius: 4,
  }
}));

export default function TaxBreakdownModal({
  open,
  type,
  context,
  data,
  paymentMethod,
  startDate,
  endDate,
  issuerName,
  onClose
}: TaxBreakdownModalProps) {
  const classes = useStyles();

  const { processed_data, config } = useTaxBreakdownModal({
    data,
    type,
    context,
    paymentMethod,
    startDate,
    endDate,
    issuerName
  });

  const renderTaxItems = (items: ProcessedTaxItem[], is_warning: boolean = false) => {
    if (items.length === 0) return null;

    return items.map((item, index) => (
      <Box
        key={`${item.tax_type}-${item.rate}-${index}`}
        className={is_warning ? classes.cancellationItem : classes.taxItem}
      >
        <Typography
          className={is_warning ? classes.cancellationLabel : classes.taxLabel}
        >
          {formatTaxLine(item)}
        </Typography>
        <Typography
          className={is_warning ? classes.cancellationAmount : classes.taxAmount}
        >
          {formatTaxAmount(item.amount, item.currency)}
        </Typography>
      </Box>
    ));
  };

  const renderCurrencySection = (currency_data: ProcessedTaxData) => {
    const { 
      currency, 
      currency_name, 
      total_pending, 
      total_cancellation, 
      pending_items, 
      cancellation_items 
    } = currency_data;
    
    const show_cancellation = context !== 'unregistered' && cancellation_items.length > 0;

    return (
      <Box key={currency} className={classes.currencySection}>
        <Box className={classes.currencyHeader}>
          <Typography className={classes.currencyTitle}>
            {currency} ({currency_name})
          </Typography>
          <Typography className={classes.currencyTotal}>
            {formatTaxAmount(total_pending, currency)}
          </Typography>
        </Box>

        <Box className={classes.taxItemsContainer}>
          {renderTaxItems(pending_items)}
        </Box>

        {show_cancellation && (
          <Box className={classes.cancellationSection}>
            <Box className={classes.cancellationHeader}>
              <Typography className={classes.cancellationTitle}>
                {translate("cfdis.cfdis_non_integrated.modals.common.cancellation_request_section")}
              </Typography>
              <Typography className={classes.cancellationTotal}>
                {formatTaxAmount(total_cancellation, currency)}
              </Typography>
            </Box>
            <Box className={classes.taxItemsContainer}>
              {renderTaxItems(cancellation_items, true)}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderContent = () => {
    if (processed_data.is_empty) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="body1">
            {config.empty_message}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {config.subtitle && (
          <Typography className={classes.subtitleText}>
            {config.subtitle}
          </Typography>
        )}
        {processed_data.currencies.map((currency_data, index) => (
          <React.Fragment key={currency_data.currency}>
            {renderCurrencySection(currency_data)}
            {index < processed_data.currencies.length - 1 && (
              <Box className={classes.dividerSection}>
                <Divider />
              </Box>
            )}
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth="md"
      fullWidth
      aria-labelledby="tax-breakdown-dialog-title"
    >
      <DialogTitle id="tax-breakdown-dialog-title" className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          {config.title}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {renderContent()}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button 
          onClick={onClose} 
          color="primary" 
          variant="outlined"
          className={classes.acceptButton}
        >
          Aceptar
        </Button>
      </DialogActions>
    </Dialog>
  );
}